import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloseIcon from '@material-ui/icons/Close'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import RefreshIcon from '@material-ui/icons/Refresh'
import SaveIcon from '@material-ui/icons/Save'
import SearchIcon from '@material-ui/icons/Search'
import VisibilityIcon from '@material-ui/icons/Visibility'

const HMI_GREEN = '#2b7a2f'
const HMI_ORANGE = '#d86f00'
const HMI_RED = '#b33a2b'
const HMI_GOLD = '#9b6a00'
const LOGO_URL = '/build/640a56404965b71a9edd0e7d_Logo.png'

const nextMonth = () => {
  const now = new Date()
  return `${new Date(now.getFullYear(), now.getMonth() + 1, 1).getFullYear()}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 1).getMonth() + 1).padStart(2, '0')}`
}

const currency = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const selectionKey = selection =>
  `${selection.aspirePropertyId}-${selection.opportunityId || 'none'}`

const SummaryCard = ({ label, value, accent = HMI_GREEN }) => (
  <Card variant="outlined" style={{ minWidth: 180, flex: '1 1 180px', borderTop: `4px solid ${accent}` }}>
    <CardContent style={{ paddingBottom: 14 }}>
      <Typography variant="caption" style={{ color: '#6f796e', fontWeight: 700, letterSpacing: 0.8 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="h5" style={{ color: accent, fontWeight: 800, marginTop: 4 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
)

const issueStyle = severity => {
  if (severity === 'error') return { background: '#f8ded9', color: HMI_RED }
  if (severity === 'warning') return { background: '#fff0c9', color: HMI_GOLD }
  if (severity === 'success') return { background: '#dfeeda', color: HMI_GREEN }
  return { background: '#e8ece7', color: '#526052' }
}

const WorkTicketDialog = ({ row, onClose }) => (
  <Dialog open={!!row} onClose={onClose} maxWidth="lg" fullWidth>
    {row && (
      <>
        <DialogTitle disableTypography style={{ borderTop: `5px solid ${HMI_GREEN}` }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                Forecast Details
              </Typography>
              <Typography variant="body1">{row.propertyName}</Typography>
              <Typography variant="body2" color="textSecondary">
                Opportunity {row.opportunityNumber || row.opportunityId || 'not selected'}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close forecast details"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexWrap="wrap" mb={2} style={{ gap: 8 }}>
            <Chip label={`${row.frequency.label} · ${row.frequency.source}`} />
            <Chip label={`Expected ${row.expectedVisits}`} />
            <Chip label={`Completed ${row.completedCount}`} />
            <Chip label={`Canceled ${row.canceledCount}`} />
            <Chip label={`Expected ${currency(row.expectedInvoiceTotal)}`} />
          </Box>
          <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Expected visit dates</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {(row.expectedDates || []).join(', ') || 'No occurrence is forecast for this month.'}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow style={{ background: HMI_GREEN }}>
                  {['Work Ticket', 'Anticipated', 'Scheduled', 'Completed', 'Status', 'Invoice', 'Amount', ''].map(header => (
                    <TableCell key={header} style={{ color: '#fff', fontWeight: 700 }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(row.workTickets || []).map(ticket => (
                  <TableRow key={ticket.workTicketId}>
                    <TableCell>{ticket.workTicketNumber || ticket.workTicketId}</TableCell>
                    <TableCell>{ticket.anticipatedDate || '—'}</TableCell>
                    <TableCell>{ticket.scheduledDate || '—'}</TableCell>
                    <TableCell>{ticket.completeDate || '—'}</TableCell>
                    <TableCell>{ticket.statusName || '—'}</TableCell>
                    <TableCell>{ticket.invoiceId || '—'}</TableCell>
                    <TableCell>{currency(ticket.invoicedAmount || ticket.price)}</TableCell>
                    <TableCell>
                      <IconButton
                        component="a"
                        href={ticket.aspireUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        title="Open WorkTicket in Aspire"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!(row.workTickets || []).length && (
                  <TableRow><TableCell colSpan={8} align="center">No matching billable WorkTickets were found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={row.aspirePropertyUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Aspire Property
          </Button>
          {row.aspireOpportunityUrl && (
            <Button
              component="a"
              href={row.aspireOpportunityUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              Aspire Opportunity
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
)

const CandidateTable = ({ candidates, onChange }) => {
  const changeCandidate = (key, patch) => {
    onChange(candidates.map(candidate =>
      selectionKey(candidate) === key ? { ...candidate, ...patch } : candidate
    ))
  }

  return (
    <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 560 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Include', 'Property', 'Opportunity', 'Billing', 'Annual Occ.', 'Rate', 'Reason if excluded'].map(header => (
              <TableCell key={header} style={{ background: HMI_GREEN, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map(candidate => {
            const key = selectionKey(candidate)
            const canInclude = !!candidate.opportunityId
            return (
              <TableRow key={key} hover style={{ opacity: candidate.included ? 1 : 0.72 }}>
                <TableCell>
                  <Checkbox
                    color="primary"
                    checked={candidate.included === true}
                    disabled={!canInclude}
                    onChange={event => changeCandidate(key, {
                      included: event.target.checked,
                      exclusionReason: event.target.checked ? '' : candidate.exclusionReason
                    })}
                    inputProps={{ 'aria-label': `Include property ${candidate.aspirePropertyId}` }}
                  />
                </TableCell>
                <TableCell style={{ minWidth: 250 }}>
                  <Link href={candidate.aspirePropertyUrl} target="_blank" rel="noopener noreferrer" style={{ color: HMI_GREEN, fontWeight: 700 }}>
                    {candidate.aspirePropertyId}
                  </Link>
                  <Typography variant="body2">{candidate.propertyName || 'Aspire Property'}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {candidate.propertyStatusName || 'Unknown status'}
                  </Typography>
                </TableCell>
                <TableCell style={{ minWidth: 230 }}>
                  {candidate.aspireOpportunityUrl
                    ? (
                      <Link href={candidate.aspireOpportunityUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2c3b2c' }}>
                        {candidate.opportunityNumber || candidate.opportunityId} · {candidate.opportunityName}
                      </Link>
                      )
                    : <Typography variant="body2" style={{ color: HMI_RED }}>No active maintenance opportunity</Typography>}
                </TableCell>
                <TableCell>{candidate.billingType || '—'}</TableCell>
                <TableCell align="right">{candidate.annualOccurrences || 0}</TableCell>
                <TableCell align="right">{currency(candidate.perVisitRate)}</TableCell>
                <TableCell style={{ minWidth: 250 }}>
                  {!candidate.included && (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={candidate.exclusionReason || ''}
                      onChange={event => changeCandidate(key, { exclusionReason: event.target.value })}
                      placeholder="Canceled contract, billed separately…"
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const ForecastTable = ({ snapshot, exceptionsOnly, onViewDetails }) => {
  const rows = (snapshot.rows || []).filter(row =>
    !exceptionsOnly || row.issues.some(item => item.severity === 'error' || item.severity === 'warning')
  )
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow style={{ background: HMI_GREEN }}>
            {['Property / Opportunity', 'Routing Prescription', 'Expected', 'Aspire Tickets', 'Expected $', 'Aspire $', 'Status', ''].map(header => (
              <TableCell key={header} style={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={`${row.propertyId}-${row.opportunityId || 'none'}`} hover style={{ opacity: row.included ? 1 : 0.68 }}>
              <TableCell style={{ minWidth: 265 }}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Link href={row.aspirePropertyUrl} target="_blank" rel="noopener noreferrer" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                    {row.propertyId}
                  </Link>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>{row.propertyName}</Typography>
                </Box>
                {row.aspireOpportunityUrl && (
                  <Link href={row.aspireOpportunityUrl} target="_blank" rel="noopener noreferrer" variant="caption">
                    Opp {row.opportunityNumber || row.opportunityId} · {row.opportunityName}
                  </Link>
                )}
              </TableCell>
              <TableCell style={{ minWidth: 200 }}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>{row.frequency.label}</Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  {(row.preferredDays || []).join(', ') || 'No service day'} · {row.frequency.source}
                </Typography>
                {(row.accessHours.earliest || row.accessHours.latest) && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    Access {row.accessHours.earliest || 'open'}–{row.accessHours.latest || 'open'}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="h6" style={{ fontWeight: 800 }}>{row.expectedVisits}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {(row.expectedDates || []).join(', ') || 'No dates forecast'}
                </Typography>
              </TableCell>
              <TableCell style={{ minWidth: 180 }}>
                <Typography variant="body2">Completed <strong>{row.completedCount}</strong></Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  Scheduled {row.scheduledCount} · Open {row.openCount} · Canceled {row.canceledCount}
                </Typography>
              </TableCell>
              <TableCell align="right" style={{ fontWeight: 700 }}>{currency(row.expectedInvoiceTotal)}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" style={{ fontWeight: 700 }}>{currency(row.actualInvoiceTotal)}</Typography>
                {Math.abs(row.variance) >= 0.01 && (
                  <Typography variant="caption" style={{ color: HMI_RED }}>{currency(row.variance)} variance</Typography>
                )}
              </TableCell>
              <TableCell style={{ minWidth: 215 }}>
                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                  {(row.issues || []).map(item => (
                    <Chip
                      key={item.code}
                      size="small"
                      label={item.label}
                      style={{ ...issueStyle(item.severity), height: 23, fontSize: 11 }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => onViewDetails(row)}
                  style={{ color: HMI_GREEN }}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow><TableCell colSpan={8} align="center">No rows match the current filter.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const MaintenanceBillingForecastPage = ({ url }) => {
  const defaultMonth = useMemo(nextMonth, [])
  const endpoint = `${url}/api/aspire/routing/maintenance-invoice-groups`
  const propertyGroupsEndpoint = `${url}/api/aspire/routing/property-groups`
  const invoiceGroupValuesEndpoint = `${url}/api/aspire/routing/maintenance-invoice-group-values`
  const [groups, setGroups] = useState([])
  const [propertyGroups, setPropertyGroups] = useState([])
  const [invoiceGroupValues, setInvoiceGroupValues] = useState([])
  const [invoiceGroupDefinition, setInvoiceGroupDefinition] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [groupName, setGroupName] = useState('')
  const [notes, setNotes] = useState('')
  const [propertyGroupId, setPropertyGroupId] = useState('')
  const [customFieldValue, setCustomFieldValue] = useState('')
  const [month, setMonth] = useState(defaultMonth)
  const [discovery, setDiscovery] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [forecast, setForecast] = useState(null)
  const [detailRow, setDetailRow] = useState(null)
  const [exceptionsOnly, setExceptionsOnly] = useState(false)
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [loadingPropertyGroups, setLoadingPropertyGroups] = useState(true)
  const [loadingInvoiceGroupValues, setLoadingInvoiceGroupValues] = useState(true)
  const [discovering, setDiscovering] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncingAspire, setSyncingAspire] = useState(false)
  const [forecasting, setForecasting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const loadGroups = async (preferredId = '') => {
    setLoadingGroups(true)
    try {
      const response = await axios.get(endpoint)
      const nextGroups = response.data || []
      setGroups(nextGroups)
      const nextId = preferredId || selectedGroupId
      if (nextId && nextGroups.some(group => group._id === nextId)) setSelectedGroupId(nextId)
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message)
    } finally {
      setLoadingGroups(false)
    }
  }

  const loadPropertyGroups = async () => {
    setLoadingPropertyGroups(true)
    try {
      const response = await axios.get(propertyGroupsEndpoint, { params: { branchId: 5 } })
      setPropertyGroups(response.data?.groups || [])
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message)
    } finally {
      setLoadingPropertyGroups(false)
    }
  }

  const loadInvoiceGroupValues = async () => {
    setLoadingInvoiceGroupValues(true)
    try {
      const response = await axios.get(invoiceGroupValuesEndpoint, { params: { branchId: 5 } })
      setInvoiceGroupDefinition(response.data?.definition || null)
      setInvoiceGroupValues(response.data?.values || [])
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message)
    } finally {
      setLoadingInvoiceGroupValues(false)
    }
  }

  useEffect(() => {
    loadGroups()
    loadPropertyGroups()
    loadInvoiceGroupValues()
  }, [])

  useEffect(() => {
    const group = groups.find(item => item._id === selectedGroupId)
    if (!group) return
    setGroupName(group.name || '')
    setNotes(group.notes || '')
    setPropertyGroupId(group.source?.aspirePropertyGroupId ? String(group.source.aspirePropertyGroupId) : '')
    setCustomFieldValue(
      group.aspireSync?.customFieldValue ||
      group.source?.aspireCustomFieldValue ||
      ''
    )
    setCandidates((group.opportunitySelections || []).map(selection => ({
      ...selection,
      aspirePropertyUrl: `https://cloud.youraspire.com/app/properties/overview/${selection.aspirePropertyId}`,
      aspireOpportunityUrl: selection.opportunityId
        ? `https://cloud.youraspire.com/app/opportunities/details/${selection.opportunityId}`
        : null
    })))
    setDiscovery({
      source: group.source || { type: 'manual' },
      aspireCompanyId: group.aspireCompanyId || null,
      customerName: group.customerName || ''
    })
    setForecast(null)
    setError('')
    setMessage('')
  }, [groups, selectedGroupId])

  const newGroup = () => {
    setSelectedGroupId('')
    setGroupName('')
    setNotes('')
    setPropertyGroupId('')
    setCustomFieldValue('')
    setCandidates([])
    setDiscovery(null)
    setForecast(null)
    setError('')
    setMessage('')
  }

  const discover = async () => {
    setDiscovering(true)
    setError('')
    setMessage('')
    try {
      const group = groups.find(item => item._id === selectedGroupId)
      const body = {
        month,
        branchId: 5,
        propertyGroupId: propertyGroupId ? Number(propertyGroupId) : undefined,
        customFieldDefinitionId: !propertyGroupId
          ? invoiceGroupDefinition?.propertyCustomFieldDefinitionId
          : undefined,
        customFieldValue: !propertyGroupId ? customFieldValue.trim() : undefined,
        propertyIds: !propertyGroupId && !customFieldValue.trim() && group
          ? group.memberAspirePropertyIds
          : undefined
      }
      const response = await axios.post(`${endpoint}/discover`, body)
      setDiscovery(response.data)
      setCandidates(response.data.candidates || [])
      if (!groupName) {
        setGroupName(
          response.data.source?.aspirePropertyGroupName ||
          response.data.source?.aspireCustomFieldValue ||
          'Maintenance Invoice Group'
        )
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message)
    } finally {
      setDiscovering(false)
    }
  }

  const saveGroup = async ({ silent = false } = {}) => {
    if (!groupName.trim()) {
      setError('Enter a maintenance invoice group name.')
      return ''
    }
    const memberIds = [...new Set(candidates.map(candidate => Number(candidate.aspirePropertyId)).filter(Number.isFinite))]
    if (memberIds.length < 1) {
      setError('Discover or select at least one property before saving.')
      return ''
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const body = {
        name: groupName.trim(),
        notes: notes.trim(),
        active: true,
        memberAspirePropertyIds: memberIds,
        source: discovery?.source || { type: 'manual' },
        aspireCompanyId: discovery?.aspireCompanyId || null,
        customerName: discovery?.customerName || '',
        aspireSync: {
          customFieldDefinitionId: invoiceGroupDefinition?.propertyCustomFieldDefinitionId || null,
          customFieldName: invoiceGroupDefinition?.name || 'MTC Invoice Group',
          customFieldValue: customFieldValue.trim(),
          lastSyncedAt: groups.find(group => group._id === selectedGroupId)?.aspireSync?.lastSyncedAt || null
        },
        opportunitySelections: candidates.map(candidate => ({
          aspirePropertyId: candidate.aspirePropertyId,
          propertyNumber: candidate.propertyNumber || '',
          propertyName: candidate.propertyName || '',
          opportunityId: candidate.opportunityId || null,
          opportunityNumber: candidate.opportunityNumber || '',
          opportunityName: candidate.opportunityName || '',
          included: candidate.included === true,
          exclusionReason: candidate.included ? '' : (candidate.exclusionReason || ''),
          billingType: candidate.billingType || '',
          startDate: candidate.startDate || null,
          endDate: candidate.endDate || null,
          annualOccurrences: candidate.annualOccurrences || 0,
          annualAmount: candidate.annualAmount || 0,
          perVisitRate: candidate.perVisitRate || 0,
          primaryOpportunityServiceId: candidate.primaryOpportunityServiceId || null,
          opportunityServiceIds: candidate.opportunityServiceIds || []
        }))
      }
      const response = selectedGroupId
        ? await axios.put(`${endpoint}/${selectedGroupId}`, body)
        : await axios.post(endpoint, body)
      const savedId = response.data._id || selectedGroupId
      if (!silent) setMessage(`Saved ${response.data.name}.`)
      await loadGroups(savedId)
      return savedId
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message)
      return ''
    } finally {
      setSaving(false)
    }
  }

  const syncToAspire = async () => {
    if (!selectedGroupId || !customFieldValue.trim()) {
      setError('Save the group and choose an Aspire MTC Invoice Group value first.')
      return
    }
    const includedPropertyCount = new Set(
      candidates
        .filter(candidate => candidate.included === true)
        .map(candidate => Number(candidate.aspirePropertyId))
    ).size
    if (!window.confirm(
      `Publish "${customFieldValue.trim()}" to ${includedPropertyCount} included Aspire properties? Existing assignments to another value will be left unchanged and reported as conflicts.`
    )) return

    setSyncingAspire(true)
    setError('')
    setMessage('')
    try {
      const savedId = await saveGroup({ silent: true })
      if (!savedId) return
      const response = await axios.post(`${endpoint}/${savedId}/sync-aspire`, {
        customFieldValue: customFieldValue.trim(),
        overwrite: false
      })
      setMessage(
        `Aspire membership synced: ${response.data.createdCount} added, ${response.data.updatedCount} updated, ` +
        `${response.data.unchangedCount} already current.`
      )
      await Promise.all([
        loadGroups(savedId),
        loadInvoiceGroupValues()
      ])
    } catch (requestError) {
      const conflicts = requestError.response?.data?.conflicts || []
      const detail = conflicts.length ? ` (${conflicts.length} conflicts; nothing was overwritten)` : ''
      setError(
        (requestError.response?.data?.message || requestError.response?.data?.error || requestError.message) + detail
      )
    } finally {
      setSyncingAspire(false)
    }
  }

  const buildForecast = async () => {
    if (!selectedGroupId) {
      setError('Save or select a maintenance invoice group first.')
      return
    }
    setForecasting(true)
    setError('')
    setMessage('')
    try {
      const response = await axios.post(`${endpoint}/${selectedGroupId}/forecast`, {
        month
      })
      setForecast(response.data)
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message)
    } finally {
      setForecasting(false)
    }
  }

  const exportForecast = async () => {
    if (!selectedGroupId || !forecast) return
    setExporting(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/${selectedGroupId}/forecast-export`, {
        month: forecast.period.month,
        exceptionsOnly
      }, { responseType: 'blob' })
      const disposition = response.headers['content-disposition'] || ''
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] ||
        `maintenance-worktickets-${forecast.period.month}.xlsx`
      const objectUrl = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }))
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000)
    } catch (requestError) {
      if (requestError.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await requestError.response.data.text())
          setError(body.message || body.error || requestError.message)
        } catch (parseError) {
          setError(requestError.message)
        }
      } else {
        setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message)
      }
    } finally {
      setExporting(false)
    }
  }

  return (
    <Container maxWidth="xl" style={{ paddingTop: 28, paddingBottom: 64 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={3}>
        <Box display="flex" alignItems="center">
          <img src={LOGO_URL} alt="HMI" style={{ width: 170, maxHeight: 64, objectFit: 'contain', objectPosition: 'left center' }} />
          <Box ml={3} pl={3} style={{ borderLeft: `3px solid ${HMI_ORANGE}` }}>
            <Typography variant="h4" style={{ color: HMI_GREEN, fontWeight: 800 }}>
              Maintenance Billing Forecast
            </Typography>
            <Typography variant="body2" style={{ color: '#6d776b', marginTop: 4 }}>
              Define the invoice population, preview expected service, and resolve Aspire exceptions.
            </Typography>
          </Box>
        </Box>
        <Button href="/maintenance-invoice-packets" variant="outlined" style={{ color: HMI_GREEN, borderColor: HMI_GREEN }}>
          Supporting Packets
        </Button>
      </Box>

      {error && <Box mb={2}><Alert severity="error">{error}</Alert></Box>}
      {message && <Box mb={2}><Alert severity="success">{message}</Alert></Box>}

      <Paper variant="outlined" style={{ padding: 22, borderTop: `5px solid ${HMI_GREEN}`, marginBottom: 22 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2}>
          <Box>
            <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>1. Define the maintenance invoice group</Typography>
            <Typography variant="body2" color="textSecondary">
              Seed membership from an Aspire property group, then publish the included properties to the MTC Invoice Group custom field.
            </Typography>
          </Box>
          <Button onClick={newGroup} variant="outlined" style={{ color: HMI_ORANGE, borderColor: HMI_ORANGE }}>
            New Group
          </Button>
        </Box>

        <Box display="grid" style={{ gridTemplateColumns: 'minmax(210px, 1fr) minmax(210px, 1fr) minmax(280px, 1.35fr) minmax(280px, 1.35fr)', gap: 14 }}>
          <TextField
            select
            variant="outlined"
            label="Saved invoice group"
            value={selectedGroupId}
            onChange={event => setSelectedGroupId(event.target.value)}
            disabled={loadingGroups}
            helperText={loadingGroups ? 'Loading groups…' : `${groups.length} active group${groups.length === 1 ? '' : 's'}`}
          >
            <MenuItem value=""><em>New / unsaved group</em></MenuItem>
            {groups.map(group => <MenuItem key={group._id} value={group._id}>{group.name}</MenuItem>)}
          </TextField>
          <TextField
            variant="outlined"
            label="Invoice group name"
            value={groupName}
            onChange={event => setGroupName(event.target.value)}
          />
          <Autocomplete
            options={propertyGroups}
            value={propertyGroups.find(option => Number(option.propertyGroupId) === Number(propertyGroupId)) || null}
            getOptionLabel={option => `${option.propertyGroupName} (${option.propertyGroupId})`}
            getOptionSelected={(option, value) => Number(option.propertyGroupId) === Number(value.propertyGroupId)}
            onChange={(event, option) => {
              const nextPropertyGroupId = option ? String(option.propertyGroupId) : ''
              setPropertyGroupId(nextPropertyGroupId)
              if (!option) return
              const matchingValues = invoiceGroupValues.filter(value =>
                (value.propertyGroupIds || []).includes(Number(option.propertyGroupId))
              )
              if (matchingValues.length === 1) setCustomFieldValue(matchingValues[0].value)
            }}
            loading={loadingPropertyGroups}
            renderOption={option => (
              <Box>
                <Typography variant="body2">{option.propertyGroupName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  ID {option.propertyGroupId} · {option.propertyCount} Branch 5 propert{option.propertyCount === 1 ? 'y' : 'ies'}
                  {!option.active && ' · inactive'}
                </Typography>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Seed from Aspire property group"
                helperText={`${propertyGroups.length} groups represented in Branch 5`}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPropertyGroups ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <Autocomplete
            freeSolo
            options={invoiceGroupValues}
            value={customFieldValue || null}
            inputValue={customFieldValue}
            getOptionLabel={option => typeof option === 'string' ? option : option.value}
            onChange={(event, option) => setCustomFieldValue(
              typeof option === 'string' ? option : (option?.value || '')
            )}
            onInputChange={(event, value) => setCustomFieldValue(value)}
            loading={loadingInvoiceGroupValues}
            renderOption={option => (
              <Box>
                <Typography variant="body2">{option.value}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.propertyCount} Branch 5 propert{option.propertyCount === 1 ? 'y' : 'ies'}
                </Typography>
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Aspire MTC invoice group"
                helperText={invoiceGroupDefinition
                  ? `Custom field #${invoiceGroupDefinition.propertyCustomFieldDefinitionId} · select or type a value`
                  : 'MTC Invoice Group custom field was not found'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingInvoiceGroupValues ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Box>
        <Box display="flex" alignItems="flex-start" mt={2} style={{ gap: 14 }}>
          <TextField
            variant="outlined"
            type="month"
            label="Membership month"
            value={month}
            onChange={event => setMonth(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            variant="outlined"
            label="Group notes"
            value={notes}
            onChange={event => setNotes(event.target.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={discover}
            disabled={discovering || (!propertyGroupId && !customFieldValue.trim() && !selectedGroupId)}
            startIcon={discovering ? <CircularProgress size={17} color="inherit" /> : <RefreshIcon />}
            style={{ height: 56, background: HMI_ORANGE, color: '#fff' }}
          >
            {discovering ? 'Reading synced data…' : (candidates.length ? 'Refresh Preview' : 'Preview Members')}
          </Button>
        </Box>

        {discovery && (
          <Box mt={2}>
            <Alert severity="info">
              Found {discovery.propertyCount ?? new Set(candidates.map(item => item.aspirePropertyId)).size} properties and{' '}
              {discovery.opportunityCount ?? candidates.filter(item => item.opportunityId).length} maintenance opportunities.
              {(discovery.source?.aspirePropertyGroupName || discovery.source?.aspireCustomFieldValue) && (
                <> Source: {discovery.source.aspirePropertyGroupName || discovery.source.aspireCustomFieldValue}.</>
              )}
            </Alert>
            {(discovery.warnings || []).map(warning => (
              <Typography key={warning} variant="caption" display="block" style={{ color: HMI_GOLD, marginTop: 5 }}>
                • {warning}
              </Typography>
            ))}
          </Box>
        )}

        {!!candidates.length && (
          <Box mt={2}>
            <CandidateTable candidates={candidates} onChange={setCandidates} />
            <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 10 }}>
              {selectedGroupId && customFieldValue.trim() && (
                <Button
                  variant="outlined"
                  onClick={syncToAspire}
                  disabled={saving || syncingAspire}
                  startIcon={syncingAspire ? <CircularProgress size={17} color="inherit" /> : <RefreshIcon />}
                  style={{ color: HMI_ORANGE, borderColor: HMI_ORANGE }}
                >
                  {syncingAspire ? 'Publishing…' : 'Publish Membership to Aspire'}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => saveGroup()}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveIcon />}
                style={{ background: HMI_GREEN, color: '#fff' }}
              >
                {saving ? 'Saving…' : (selectedGroupId ? 'Save Group Changes' : 'Create Invoice Group')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" style={{ padding: 22, borderTop: `5px solid ${HMI_ORANGE}` }}>
        <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>2. Review a maintenance month</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 18 }}>
          The routing prescription creates the expectation. Live Aspire WorkTickets show what is ready and what needs correction.
        </Typography>
        <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 14 }}>
          <TextField
            select
            variant="outlined"
            label="Maintenance invoice group"
            value={selectedGroupId}
            onChange={event => setSelectedGroupId(event.target.value)}
            style={{ minWidth: 250 }}
          >
            <MenuItem value=""><em>Select a saved group</em></MenuItem>
            {groups.map(group => <MenuItem key={group._id} value={group._id}>{group.name}</MenuItem>)}
          </TextField>
          <TextField
            variant="outlined"
            type="month"
            label="Forecast month"
            value={month}
            onChange={event => setMonth(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={buildForecast}
            disabled={forecasting || !selectedGroupId || !month}
            startIcon={forecasting ? <CircularProgress size={17} color="inherit" /> : <SearchIcon />}
            style={{ height: 56, background: HMI_GREEN, color: '#fff' }}
          >
            {forecasting ? 'Refreshing WorkTickets…' : 'Build / Re-sync Forecast'}
          </Button>
        </Box>

        {forecast && (
          <Box mt={3}>
            <Paper elevation={0} style={{ padding: 18, background: '#f3f8ef', borderLeft: `5px solid ${HMI_ORANGE}`, marginBottom: 18 }}>
              <Typography variant="h5" style={{ color: HMI_GREEN, fontWeight: 800 }}>
                {forecast.group.name} · {forecast.period.month}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {forecast.group.customerName || 'Aspire customer'} · {forecast.group.memberCount} configured properties
                {' · Local forecast data'}
              </Typography>
            </Paper>
            <Box display="flex" flexWrap="wrap" mb={2} style={{ gap: 12 }}>
              <SummaryCard label="Included properties" value={forecast.summary.includedProperties} />
              <SummaryCard label="Expected visits" value={forecast.summary.expectedVisits} />
              <SummaryCard label="Completed" value={forecast.summary.completedCount} />
              <SummaryCard label="Canceled" value={forecast.summary.canceledCount} accent={forecast.summary.canceledCount ? HMI_RED : HMI_GREEN} />
              <SummaryCard label="Needs action" value={forecast.summary.actionCount} accent={forecast.summary.actionCount ? HMI_ORANGE : HMI_GREEN} />
              <SummaryCard label="Expected invoice" value={currency(forecast.summary.expectedInvoiceTotal)} accent={HMI_ORANGE} />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="textSecondary">
                Forecast amounts use the selected opportunity’s billing method and the property’s routing prescription.
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                <Button
                  variant="outlined"
                  onClick={exportForecast}
                  disabled={exporting}
                  startIcon={exporting ? <CircularProgress size={17} color="inherit" /> : <GetAppIcon />}
                  style={{ color: HMI_GREEN, borderColor: HMI_GREEN }}
                >
                  {exporting ? 'Building Excel…' : (exceptionsOnly ? 'Export Exceptions' : 'Export Excel')}
                </Button>
                <FormControlLabel
                  control={<Checkbox checked={exceptionsOnly} onChange={event => setExceptionsOnly(event.target.checked)} color="primary" />}
                  label="Exceptions only"
                />
              </Box>
            </Box>
            <ForecastTable snapshot={forecast} exceptionsOnly={exceptionsOnly} onViewDetails={setDetailRow} />
          </Box>
        )}
      </Paper>
      <WorkTicketDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </Container>
  )
}

const mapStateToProps = state => ({ url: state.url })

export default connect(mapStateToProps)(MaintenanceBillingForecastPage)
